import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  AfterViewInit,
  ApplicationRef,
  Component,
  ComponentRef,
  ElementRef,
  EnvironmentInjector,
  HostBinding,
  Inject,
  OnDestroy,
  Signal,
  ViewChild,
  WritableSignal,
  computed,
  createComponent,
  createEnvironmentInjector,
  inject,
  signal,
  viewChild
} from "@angular/core";
import {
  autoPlacement,
  computePosition,
  offset,
  shift,
  VirtualElement
} from "@floating-ui/dom";
import { TranslocoModule } from "@jsverse/transloco";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import {
  DxCheckBoxModule,
  DxCheckBoxTypes
} from "devextreme-angular/ui/check-box";
import { isUndefined } from "lodash";
import { BehaviorSubject, Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { sntConfirm } from "src/app/common/dialog";
import { PlatformType } from "src/app/common/platform-type";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import { ButtonModule } from "src/app/components/ui/button/button.module";
import { BeforeUnloadService } from "src/app/services/before-unload.service";
import {
  PopupButton,
  PopupComponent,
  PopupOptions
} from "src/app/services/popup/popup-component";
import { POPUP_INJECT, PopupRef } from "src/app/services/popup/popup-ref";
import { Bind } from "src/app/support/decorators/bind";
import { getMessageByError } from "src/app/support/error";
import { getErrorMessageFromHttpErrorResponse } from "src/app/support/http-error-response";
import { sntTranslate } from "src/app/support/translate";
import { PLATFORM_TOKEN } from "src/app/tokens/platform.token";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("PopupHolderComponent");
type PopupHolderStatus = "maximized" | "minimized";

const POPUP_DIALOG_TITLE_ID = "snt-popup-dialog-title";

const FOCUSABLE_SELECTOR: string = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(", ");

@Component({
  imports: [CommonModule, TranslocoModule, DxCheckBoxModule, ButtonModule],
  selector: "snt-popup-holder",
  templateUrl: "popup-holder.component.html",
  styles: `
    :host {
      display: flex;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  `
})
export class PopupHolderComponent implements AfterViewInit, OnDestroy {
  @ViewChild("componentContainer")
  componetContainerElementRef?: ElementRef<HTMLDivElement>;

  container = viewChild.required<ElementRef<HTMLDivElement>>("container");

  status = signal<PopupHolderStatus>("maximized");

  isMinimized = computed(() => this.status() === "minimized");
  isMaximized = computed(() => this.status() === "maximized");

  isOverlayVisible = computed(() => this.autoCenter());

  contentHeightRaw = signal<number | string | undefined>(undefined);
  contentHeight = computed(() => {
    const raw: number | string | undefined = this.contentHeightRaw();
    switch (true) {
      case typeof raw === "number":
        // 120px è l'altezza del header e del footer
        return `${raw + 120}px`;
      case typeof raw === "string":
        return raw;
      default:
        return undefined;
    }
  });

  contentWidthRaw = signal<number | string | undefined>(undefined);
  contentWidth = computed(() => {
    const raw: number | string | undefined = this.contentWidthRaw();
    switch (true) {
      case typeof raw === "number":
        return `${raw}px`;
      case typeof raw === "string":
        return raw;
      default:
        return undefined;
    }
  });

  fullscreen = signal<boolean>(false);

  private readonly zIndex = signal<number>(50);

  @HostBinding("class") get classesWhenMaximized(): string {
    return this.isMaximized() ? "absolute" : "";
  }

  @HostBinding("style.z-index") get hostZIndex(): number | null {
    return this.isMaximized() ? this.zIndex() : null;
  }

  protected readonly PLATFORM = inject(PLATFORM_TOKEN);
  protected readonly PlatformType = PlatformType;

  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly beforeUnloadService = inject(BeforeUnloadService);

  error = signal<string | undefined>(undefined);

  dialog: Signal<boolean>;
  hideButtonClose: Signal<boolean>;
  titleVisible: Signal<boolean>;
  backdropBlur: Signal<boolean>;
  contentOverflowHidden: Signal<boolean>;
  title: Signal<string | undefined>;
  icon: Signal<string | undefined>;
  buttons: Signal<PopupButton[]>;

  relativeTo: Signal<HTMLElement | VirtualElement | undefined>;
  autoCenter = computed<boolean>(() => {
    return !this.relativeTo();
  });

  x: WritableSignal<number | undefined>;
  y: WritableSignal<number | undefined>;

  dismissOnOverlayClick: Signal<boolean>;

  confirmOnDirtyClose: Signal<boolean>;

  secondaryButtons: Signal<PopupButton[]>;

  isCheckboxVisible: Signal<boolean>;
  checkboxLabel: Signal<string | undefined>;

  dirty$: BehaviorSubject<boolean> | undefined;
  dirtySubscription: Subscription | undefined;

  checkboxValue: boolean | undefined;

  readonly dialogTitleId: string = POPUP_DIALOG_TITLE_ID;

  private componentRef: ComponentRef<PopupComponent>;
  private previouslyFocusedElement: HTMLElement | null = null;
  private dialogKeydownHandler: ((event: KeyboardEvent) => void) | undefined;

  constructor(
    @Inject(POPUP_INJECT.REF) private popupRef: PopupRef<any>,
    @Inject(POPUP_INJECT.COMPONENT) componentType: any,
    @Inject(POPUP_INJECT.CONFIG) config: PopupOptions | undefined,
    @Inject(POPUP_INJECT.DATA) data: any
  ) {
    const newEnvInjector = createEnvironmentInjector(
      [
        {
          provide: POPUP_INJECT.REF,
          useValue: <PopupRef<any>>{
            confirm: async (result?: any) => {
              await this.onConfirm(result);
            },
            close: async (result: any) => {
              await this.onClose(result);
            },
            updateHeight: (height?: number) => {
              if (!isUndefined(height)) {
                setTimeout(() => {
                  this.contentHeightRaw.update(() => height);
                }, 0);
              }
            }
          }
        },
        { provide: POPUP_INJECT.DATA, useValue: data }
      ],
      this.environmentInjector
    );

    this.relativeTo = signal<HTMLElement | VirtualElement | undefined>(
      config?.relativeTo
    );
    this.x = signal<number | undefined>(config?.x);
    this.y = signal<number | undefined>(config?.y);

    this.dismissOnOverlayClick = signal<boolean>(
      config?.dismissOnOverlayClick || false
    );

    this.confirmOnDirtyClose = signal<boolean>(
      config?.confirmOnDirtyClose ?? true
    );

    if (config?.zIndex !== undefined) {
      this.zIndex.set(config.zIndex);
    }

    this.componentRef = createComponent(componentType, {
      environmentInjector: newEnvInjector
    });

    this.title = computed(() => {
      if (this.componentRef.instance.title) {
        return this.componentRef.instance.title();
      }

      if (this.componentRef.instance.popupTitle) {
        return (
          this.componentRef.instance.popupTitle() ||
          (this.componentRef.instance.getTitle
            ? this.componentRef.instance.getTitle()
            : "")
        );
      }

      return this.componentRef.instance.getTitle
        ? this.componentRef.instance.getTitle()
        : "";
    });

    this.dialog = computed(() => {
      return !!config?.dialog;
    });

    this.hideButtonClose = computed(() => {
      return !!config?.hideButtonClose;
    });

    this.titleVisible = computed(() => {
      return this.componentRef.instance.isTitleVisible
        ? this.componentRef.instance.isTitleVisible()
        : true;
    });

    this.backdropBlur = computed(() => {
      return !!config?.backdropBlur;
    });

    this.contentOverflowHidden = signal<boolean>(
      !!config?.contentOverflowHidden
    );

    this.icon = computed(() => {
      return config?.icon || "fa-light fa-window";
    });

    this.buttons = computed(() => {
      if (this.componentRef.instance.buttons) {
        return this.componentRef.instance.buttons();
      }

      return [];
    });

    if (this.componentRef.instance.dirty) {
      this.dirty$ = this.componentRef.instance.dirty;
      this.dirtySubscription = this.componentRef.instance.dirty
        .pipe(distinctUntilChanged())
        .subscribe((isDirty: boolean) => {
          logger.debug(`PopupHolder effect isDirt ${isDirty}`);

          if (isDirty) {
            this.beforeUnloadService.acquire();
          } else {
            this.beforeUnloadService.release();
          }
        });
    }

    this.fullscreen.set(!!config?.fullscreen);

    if (config?.width) {
      this.contentWidthRaw.set(config.width);
    }

    if (config?.height) {
      this.contentHeightRaw.set(config.height);
    }

    this.secondaryButtons = computed(() => {
      if (this.componentRef.instance.secondaryButtons) {
        return this.componentRef.instance.secondaryButtons();
      }

      return [];
    });

    this.isCheckboxVisible = signal<boolean>(
      this.componentRef.instance.isCustomCheckboxVisible
        ? this.componentRef.instance.isCustomCheckboxVisible()
        : false
    );

    this.checkboxLabel = signal<string | undefined>(
      this.componentRef.instance.getCustomCheckboxLabel
        ? this.componentRef.instance.getCustomCheckboxLabel()
        : undefined
    );
  }

  ngAfterViewInit(): void {
    this.componetContainerElementRef?.nativeElement.append(
      this.componentRef.location.nativeElement
    );
    this.appRef.attachView(this.componentRef.hostView);

    if (this.dialog() && this.isMaximized()) {
      this.setupDialogAccessibility();
    }

    if (this.relativeTo()) {
      computePosition(this.relativeTo()!, this.container().nativeElement, {
        middleware: [
          autoPlacement({
            alignment: null,
            allowedPlacements: ["right", "left"]
          }),
          offset({ mainAxis: 8 }),
          shift()
        ]
      }).then(({ x, y }) => {
        this.x.set(x);
        this.y.set(y);
      });
    }
  }

  ngOnDestroy(): void {
    this.teardownDialogAccessibility();
    this.dirtySubscription?.unsubscribe();
    this.componentRef.destroy();
  }

  @Bind
  onDocumentClick(): void {
    this.onClose();
  }

  onOverlayClick(): void {
    if (this.dismissOnOverlayClick()) {
      this.onClose();
    }
  }

  onContainerClick(event: MouseEvent): void {
    if (this.dismissOnOverlayClick()) {
      event.stopImmediatePropagation();
    }

    if (this.isMaximized()) {
      this.status.set("maximized");
    }
  }

  async canClose(): Promise<boolean> {
    if (this.componentRef.instance.canClose) {
      return await this.componentRef.instance.canClose();
    }

    return true;
  }

  onCheckboxValueChanged(dxEvent: DxCheckBoxTypes.ValueChangedEvent): void {
    this.checkboxValue = dxEvent.value;
  }

  async onClose(data?: any): Promise<void> {
    if (this.confirmOnDirtyClose() && this.dirty$?.getValue()) {
      const result = await sntConfirm(
        sntTranslate(marker("Attenzione")),
        sntTranslate(marker("Sei sicuro di voler chiudere questa finestra?"))
      );

      if (!result) {
        return;
      }

      this.beforeUnloadService.release();
    }

    if (this.componentRef.instance.onClosed) {
      this.componentRef.instance.onClosed();
    }

    this.popupRef.close(data);
  }

  async onConfirm(data?: any): Promise<void> {
    this.popupRef.confirm(data);
  }

  async executeButtonClick(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    popupButton: PopupButton
  ): Promise<void> {
    this.error.set(undefined);

    try {
      await popupButton.onClick(buttonClickAsyncEvent, {
        checkboxValue: this.checkboxValue
      });
    } catch (ex: any) {
      // TODO gestire errore
      if (ex instanceof HttpErrorResponse) {
        this.error.set(getErrorMessageFromHttpErrorResponse(ex));
      } else {
        this.error.set(getMessageByError(<Error>ex));
      }
    }
  }

  private setupDialogAccessibility(): void {
    const container: HTMLDivElement = this.container().nativeElement;
    this.previouslyFocusedElement =
      document.activeElement as HTMLElement | null;

    this.dialogKeydownHandler = (event: KeyboardEvent): void => {
      if (event.key !== "Tab") {
        return;
      }

      const focusableElements: HTMLElement[] =
        this.getFocusableElements(container);

      if (focusableElements.length === 0) {
        return;
      }

      const firstElement: HTMLElement = focusableElements[0];
      const lastElement: HTMLElement =
        focusableElements[focusableElements.length - 1];
      const activeElement: Element | null = document.activeElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();

        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    container.addEventListener("keydown", this.dialogKeydownHandler);

    setTimeout(() => {
      const titleElement: HTMLElement | null = container.querySelector(
        `#${POPUP_DIALOG_TITLE_ID}`
      );
      const focusableElements: HTMLElement[] =
        this.getFocusableElements(container);

      if (titleElement) {
        titleElement.setAttribute("tabindex", "-1");
        titleElement.focus();
      } else if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    });
  }

  private teardownDialogAccessibility(): void {
    const container: HTMLDivElement | undefined =
      this.container()?.nativeElement;

    if (container && this.dialogKeydownHandler) {
      container.removeEventListener("keydown", this.dialogKeydownHandler);
    }

    this.dialogKeydownHandler = undefined;
    this.previouslyFocusedElement?.focus();
    this.previouslyFocusedElement = null;
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter((element: HTMLElement) => element.getClientRects().length > 0);
  }
}
